import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence, useScroll, useMotionValueEvent, useSpring, useTransform } from 'framer-motion';
import { Link } from 'react-scroll';
import { useQuery } from 'convex/react';
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from '../../utils/rtl';
import { api } from '../../convex/_generated/api';
import { Id } from '../../convex/_generated/dataModel';
import { NavigationConfig } from '../../types/navigation';

interface NavigationProps {
  language?: string;
  projectId: Id<"projects">;
}

const navVariants = {
  hidden: { y: -100, opacity: 0 },
  visible: { 
    y: 0, 
    opacity: 1,
    transition: { 
      type: "spring",
      stiffness: 100,
      damping: 20
    }
  }
};

const mobileMenuVariants = {
  closed: {
    height: 0,
    opacity: 0,
    transition: {
      staggerChildren: 0.05,
      staggerDirection: -1
    }
  },
  open: {
    height: 'auto',
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.1
    }
  }
};

const menuItemVariants = {
  closed: { x: -20, opacity: 0 },
  open: { x: 0, opacity: 1 }
};

export default function Navigation({ language = "en", projectId }: NavigationProps) {
  const [activeSection, setActiveSection] = useState('hero');
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const { scrollY, scrollYProgress } = useScroll();
  const springScrollY = useSpring(scrollY, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  // Fetch navigation settings for this project and language
  const navigationConfig = useQuery(api.landingPageSettings.getNavigationMenuItems, {
    projectId,
    languageCode: language,
  }) as NavigationConfig | undefined;

  // RTL detection and classes
  const rtlClasses = getRTLClasses(language);
  const dirAttribute = getDirAttribute(language);

  // Smooth background opacity based on scroll - navbar always visible but changes style
  const bgOpacity = useTransform(springScrollY, [0, 100], [0.5, 1]);
  const navBackground = useTransform(
    bgOpacity,
    (opacity) => `rgba(255, 255, 255, ${opacity})`
  );

  useMotionValueEvent(scrollY, "change", (latest) => {
    setIsScrolled(latest > 50);
  });

  useEffect(() => {
    if (!navigationConfig?.menuItems) return;

    const observers = new Map();

    navigationConfig.menuItems.forEach(({ id }) => {
      const element = document.getElementById(id);
      if (element) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              setActiveSection(id);
            }
          },
          { 
            threshold: 0.3,
            rootMargin: '-100px 0px -50% 0px' // Better detection for sections
          }
        );

        observer.observe(element);
        observers.set(id, observer);
      }
    });

    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
  }, [navigationConfig?.menuItems]);

  // Function to handle scroll to form sections
  const scrollToForm = (formType: 'download' | 'lead') => {
    if (formType === 'download') {
      // Trigger download form modal from hero section
      const heroDownloadBtn = document.querySelector('[data-download-trigger]') as HTMLButtonElement;
      if (heroDownloadBtn) {
        heroDownloadBtn.click();
      }
    } else {
      // Scroll to lead form section
      const leadFormSection = document.getElementById('leadForm');
      if (leadFormSection) {
        leadFormSection.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  // Loading state while fetching navigation config
  if (!navigationConfig) {
    return (
      <motion.nav
        className={`fixed top-0 left-0 right-0 z-[1000] bg-white/90 backdrop-blur-sm border-b border-white/10 ${rtlClasses}`}
        dir={dirAttribute}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="animate-pulse bg-gray-200 h-6 w-32 rounded"></div>
            <div className="hidden md:flex space-x-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="animate-pulse bg-gray-200 h-4 w-16 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </motion.nav>
    );
  }

  return (
    <>
      {/* Full Screen Backdrop Overlay - Outside of navbar */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-md z-[999] md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />
        )}
      </AnimatePresence>

      <motion.nav
        variants={navVariants}
        initial="hidden"
        animate="visible" // Always visible now - no hide/show logic
        style={{ 
          backgroundColor: navBackground,
          backdropFilter: "blur(8px)",
        }}
        className={`fixed top-0 left-0 right-0 z-[1000] transition-all duration-300 border-b border-white/10 ${rtlClasses}`}
        dir={dirAttribute}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            {navigationConfig.brandingSettings?.showLogo && (
              <motion.div 
                className="flex-shrink-0"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link
                  to="hero"
                  spy={true}
                  smooth={true}
                  offset={-64}
                  duration={500}
                  className="cursor-pointer"
                >
                  {navigationConfig.brandingSettings.logoImage ? (
                    <img 
                      src={navigationConfig.brandingSettings.logoImage} 
                      alt={navigationConfig.brandingSettings.logoText}
                      className="h-8 w-auto"
                    />
                  ) : (
                    <span className="text-xl font-bold bg-gradient-to-r from-primary to-primary/80 bg-clip-text text-transparent">
                      {navigationConfig.brandingSettings.logoText}
                    </span>
                  )}
                </Link>
              </motion.div>
            )}

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className={`flex items-center ${rtlClasses}`}>
                {navigationConfig.menuItems.map(({ id, label }) => (
                  <Link
                    key={id}
                    to={id}
                    spy={true}
                    smooth={true}
                    offset={-80} // Account for sticky navbar height
                    duration={500}
                    className="relative cursor-pointer"
                  >
                    <motion.span
                      className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        activeSection === id
                          ? 'text-primary'
                          : isScrolled ? 'text-gray-700 hover:text-primary' : 'text-white/90 hover:text-white'
                      }`}
                      whileHover={{ scale: 1.1, y: -2 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      {label}
                      {activeSection === id && (
                        <motion.div
                          layoutId="activeSection"
                          className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary"
                          initial={false}
                          transition={{
                            type: "spring",
                            stiffness: 380,
                            damping: 30
                          }}
                        />
                      )}
                    </motion.span>
                  </Link>
                ))}
              </div>
            </div>

            {/* Desktop CTA Buttons */}
            <div className="hidden md:hidden items-center space-x-3">
              <motion.button
                onClick={() => scrollToForm('download')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/90 transition-colors"
              >
                {navigationConfig.ctaButtons.primary}
              </motion.button>
              <motion.button
                onClick={() => scrollToForm('lead')}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className={`px-4 py-2 text-sm font-medium rounded-md border transition-colors ${
                  isScrolled 
                    ? 'border-primary text-primary hover:bg-primary hover:text-white' 
                    : 'border-white text-white hover:bg-white hover:text-gray-900'
                }`}
              >
                {navigationConfig.ctaButtons.secondary}
              </motion.button>
            </div>

            {/* Mobile menu button and quick contact */}
            <div className="md:hidden flex items-center gap-3">
              <Link
                to="leadForm"
                spy={true}
                smooth={true}
                offset={-64}
                duration={500}
                className="cursor-pointer"
              >
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  className="p-2 rounded-full bg-primary/10 text-primary"
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    fill="none" 
                    viewBox="0 0 24 24" 
                    strokeWidth={1.5} 
                    stroke="currentColor" 
                    className="w-5 h-5"
                  >
                    <path 
                      strokeLinecap="round" 
                      strokeLinejoin="round" 
                      d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" 
                    />
                  </svg>
                </motion.div>
              </Link>
              <motion.button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`inline-flex items-center justify-center p-2 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
                  isScrolled 
                    ? 'text-gray-700 hover:text-gray-900 hover:bg-gray-100' 
                    : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <span className="sr-only">Open main menu</span>
                <motion.div
                  className="w-6 h-6 flex flex-col justify-around"
                  animate={isMobileMenuOpen ? "open" : "closed"}
                >
                  <motion.span
                    className="w-6 h-0.5 bg-current origin-left"
                    variants={{
                      closed: { rotate: 0 },
                      open: { rotate: 45, y: 0 }
                    }}
                  />
                  <motion.span
                    className="w-6 h-0.5 bg-current"
                    variants={{
                      closed: { opacity: 1 },
                      open: { opacity: 0 }
                    }}
                  />
                  <motion.span
                    className="w-6 h-0.5 bg-current origin-left"
                    variants={{
                      closed: { rotate: 0 },
                      open: { rotate: -45, y: 0 }
                    }}
                  />
                </motion.div>
              </motion.button>
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              variants={mobileMenuVariants}
              initial="closed"
              animate="open"
              exit="closed"
              className="md:hidden bg-background/95 backdrop-blur-sm relative z-[1001] border-t border-border"
            >
              <motion.div 
                className="px-2 pt-2 pb-3 space-y-1 sm:px-3"
              >
                {navigationConfig.menuItems.map(({ id, label }) => (
                  <motion.div
                    key={id}
                    variants={menuItemVariants}
                  >
                    <Link
                      to={id}
                      spy={true}
                      smooth={true}
                      offset={-80}
                      duration={500}
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <motion.span
                        className={`block px-3 py-2 rounded-md text-base font-medium ${
                          activeSection === id
                            ? 'text-primary bg-primary/10'
                            : 'text-muted-foreground hover:text-foreground hover:bg-accent'
                        }`}
                        whileHover={{ x: 5, scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        {label}
                      </motion.span>
                    </Link>
                  </motion.div>
                ))}
                
                {/* Mobile CTA Buttons */}
                <motion.div className="pt-3 space-y-2 border-t border-border/50">
                  <motion.button
                    onClick={() => {
                      scrollToForm('download');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 bg-primary text-white text-base font-medium rounded-md hover:bg-primary/90 transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {navigationConfig.ctaButtons.primary}
                  </motion.button>
                  <motion.button
                    onClick={() => {
                      scrollToForm('lead');
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full px-3 py-2 border border-primary text-primary text-base font-medium rounded-md hover:bg-primary hover:text-white transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {navigationConfig.ctaButtons.secondary}
                  </motion.button>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-primary origin-left z-[1002]"
        style={{ scaleX: scrollYProgress }}
        initial={{ opacity: 0 }}
        animate={{ opacity: isScrolled ? 1 : 0 }}
      />
    </>
  );
} 